import { TRAITS } from "../../../constants/devices";
import { sendControl } from "../../../services/deviceControl";
import { UserService } from "../../../services/UserService";
import { settingsPayload } from "../settings/shared";

// The sensors and remote controls paired with a WZLTEG_1 security alarm
// gateway, as the app handles them (app/screens/SecurityDevices.js and
// app/screens/SecurityDeviceDetails.js). The gateway answers a command by
// updating its device record, so every read is a command followed, a few
// seconds later, by UserService.getDevice.

export const ACCESSORY_PATHS = Object.freeze({
  LIST: "/security-accessories",
  DETAIL: "/security-accessory",
});

export const KINDS = Object.freeze({ SENSOR: "sensor", REMOTE: "remote" });

export const PAGE_SIZE = 5;
export const NAME_MAX = 20;
// The app reads the record 3 s after each command, and keeps its loader up for
// 2 s after a change before it lets the user go on.
export const READ_DELAY_MS = 3000;
export const SETTLE_MS = 2000;
export const WAIT = "Please wait…";

const IMAGES = "/security_device/devices";

// acc_type → the app's label and picture (acc_type_map and acc_image_map). The
// app has no pictures of its own for water leak (4) and infrasonic (7) sensors.
export const SENSOR_TYPES = Object.freeze({
  0: { label: "Door & Window Sensor", image: `${IMAGES}/door_sensor.png` },
  1: { label: "PIR Motion Detector", image: `${IMAGES}/motion_sensor.png` },
  2: { label: "Smoke Detector", image: `${IMAGES}/smoke_sensor.png` },
  3: { label: "Gas Detector", image: `${IMAGES}/gas_sensor.png` },
  4: { label: "Water Leak Detector", image: `${IMAGES}/magnetic_door_sensor.png` },
  5: { label: "Motion & Door Sensor", image: `${IMAGES}/door_sensor.png` },
  6: { label: "Glass Break Detector", image: `${IMAGES}/glass_break_detector.png` },
  7: { label: "Infrasonic Sensor", image: `${IMAGES}/magnetic_door_sensor.png` },
  8: { label: "IR Beams/Fences", image: `${IMAGES}/ir_beams.png` },
  9: { label: "Vibration & Door Sensor", image: `${IMAGES}/window_sensor.png` },
  10: { label: "Vibration Sensor", image: `${IMAGES}/window_sensor.png` },
  11: { label: "Magnetic Door Sensor", image: `${IMAGES}/magnetic_door_sensor.png` },
});
const UNKNOWN_SENSOR = Object.freeze({ label: "Unknown Sensor", image: `${IMAGES}/door_sensor.png` });

// rm_type "0" is a keyfob; the app treats every other value as a keypad.
export const REMOTE_TYPES = Object.freeze({
  KEYFOB: { label: "Keyfob", image: `${IMAGES}/keyfob.png` },
  KEYPAD: { label: "Keypad", image: `${IMAGES}/keypad.png` },
});

// acc_zone values, in the order of the app's zone menu.
export const ZONES = Object.freeze([
  { value: "0", label: "Normal" },
  { value: "1", label: "Stay" },
  { value: "2", label: "Delay" },
  { value: "3", label: "24 Hours" },
]);

// The reference photos in the app's "+" dialog, in its order.
export const PAIRING_PHOTOS = Object.freeze([
  { src: "/security_device/accessories1.png", alt: "A door sensor and its magnet being pulled apart" },
  { src: "/security_device/accessories2.png", alt: "A door sensor and its magnet held next to each other" },
  { src: "/security_device/accessories3.png", alt: "A finger pressing the round button on an accessory" },
  { src: "/security_device/accessories4.png", alt: "A finger pressing a button on a keyfob" },
  { src: "/security_device/accessories5.png", alt: "A thumb pressing the small button on the back of an accessory" },
  { src: "/security_device/accessories6.png", alt: "A finger pressing the small button under an accessory's cover" },
]);

// ---------- Reading accessory records ----------

const blank = (value) => value === undefined || value === null || value === "";

export const idOf = (accessory, kind) => (kind === KINDS.SENSOR ? accessory?.acc_id : accessory?.rm_id);
export const nameOf = (accessory, kind) => (kind === KINDS.SENSOR ? accessory?.acc_name : accessory?.rm_name) ?? "";

export const sensorType = (sensor) => (blank(sensor?.acc_type) ? null : SENSOR_TYPES[Number(sensor.acc_type)]) ?? UNKNOWN_SENSOR;
export const remoteType = (remote) => (String(remote?.rm_type) === "0" ? REMOTE_TYPES.KEYFOB : REMOTE_TYPES.KEYPAD);
export const typeOfAccessory = (accessory, kind) => (kind === KINDS.SENSOR ? sensorType(accessory) : remoteType(accessory));

// The zone id as the zone menu holds it; the app shows Normal for anything else.
export const zoneOf = (sensor) => {
  const value = blank(sensor?.acc_zone) ? "0" : String(sensor.acc_zone);
  return ZONES.some((zone) => zone.value === value) ? value : "0";
};
export const zoneLabel = (sensor) => ZONES.find((zone) => zone.value === zoneOf(sensor)).label;

// acc_st 1 is closed and 2 open; the app shows nothing for other values.
export const sensorState = (sensor) => {
  const state = Number(sensor?.acc_st);
  if (state === 1) return "Close";
  if (state === 2) return "Open";
  return null;
};

export const batteryOf = (sensor) => (Number(sensor?.acc_bat) === 1 ? "Normal" : "Low");

// acc_wm is what the list's switch shows and sets.
export const isEnabled = (sensor) => Boolean(Number(sensor?.acc_wm));

// The pictures are masks, tinted like the app's tintColor. A stylesheet can't
// name files in /public (CRA's css-loader tries to bundle them), so the element
// passes the picture in --hx-acc-img.
export const imageStyle = (src) => ({ "--hx-acc-img": `url("${src}")` });

// ---------- Pages ----------

export const pageCount = (count) => Math.max(1, Math.ceil((count || 0) / PAGE_SIZE));

// The ids the app asks for on a page: 1-5, 6-10, … up to the count.
export const pageRequest = (page, count) => {
  const start_id = page * PAGE_SIZE - (PAGE_SIZE - 1);
  return { start_id, end_id: Math.min(start_id + PAGE_SIZE - 1, count) };
};

// The gateway's device record, or null.
export const readRecord = async (device) => {
  const response = await UserService.getDevice(device.deviceId ?? device.applianceId);
  return (Array.isArray(response) ? response[0] : response)?.device ?? null;
};

export const countOf = (record, kind) => {
  const count = Number(kind === KINDS.SENSOR ? record?.sensorAccessoriesCount : record?.remoteAccessoriesCount);
  return Number.isFinite(count) && count > 0 ? Math.floor(count) : 0;
};

// A page's accessories from the record, by id as the app filters them, once
// each and in id order.
export const pageItems = (record, kind, page) => {
  const list = kind === KINDS.SENSOR ? record?.sensorAccessories : record?.remoteAccessories;
  const first = page * PAGE_SIZE - (PAGE_SIZE - 1);
  const last = page * PAGE_SIZE;
  const byId = new Map();
  (Array.isArray(list) ? list : []).forEach((item) => {
    const id = Number(idOf(item, kind));
    if (item && id >= first && id <= last) byId.set(id, item);
  });
  return [...byId.keys()].sort((a, b) => a - b).map((id) => byId.get(id));
};

export const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// ---------- Commands ----------

// A missing trait constant would otherwise go out as trait: null; the page
// reports the thrown error instead.
const traitOf = (kind) => {
  const trait = Number(kind === KINDS.SENSOR ? TRAITS.SECURITY_ACCESSORY : TRAITS.SECURITY_REMOTE);
  if (!Number.isFinite(trait)) throw new Error(`No control trait for ${kind} accessories`);
  return trait;
};

export const sendCommand = (device, command) => sendControl(settingsPayload(device, command));

// How many accessories of a kind the gateway has.
export const countCommand = (kind) => ({ id: 1, trait: traitOf(kind), value: 0 });

// One page of them; range comes from pageRequest.
export const pageCommand = (kind, range) => ({ id: 2, trait: traitOf(kind), value: range });

// A sensor's whole record, in the app's field order. The list's switch sends it
// with a new acc_wm, the accessory page with a new name and zone.
export const sensorCommand = (sensor) => ({
  id: 3,
  trait: traitOf(KINDS.SENSOR),
  value: {
    acc_id: Number(sensor.acc_id),
    acc_wm: Number(sensor.acc_wm),
    acc_type: Number(sensor.acc_type),
    acc_zone: Number(sensor.acc_zone),
    acc_bat: Number(sensor.acc_bat),
    acc_tamper: Number(sensor.acc_tamper),
    acc_st: Number(sensor.acc_st),
    acc_name: sensor.acc_name,
  },
});

export const remoteCommand = (remote) => ({
  id: 3,
  trait: traitOf(KINDS.REMOTE),
  value: { rm_id: Number(remote.rm_id), rm_name: remote.rm_name },
});

export const deleteCommand = (kind, accessory) => ({ id: 4, trait: traitOf(kind), value: Number(idOf(accessory, kind)) });

// Puts the gateway into pairing mode (the "+" dialog opening) or out of it.
export const pairingCommand = (on) => ({ id: 4, trait: Number(TRAITS.SECURITY_MODE), value: on ? 1 : 0 });

// ---------- Between the list and an accessory ----------

// The list as it was when an accessory was opened, and what the accessory page
// then changed (the app's onChange / onDelete callbacks). Going back shows the
// list again at once with that change instead of reading the gateway again.
let savedList = null;
let lastChange = null;

export const rememberList = (snapshot) => {
  savedList = snapshot;
  lastChange = null;
};

// change: { applianceId, kind, action: "saved" | "deleted", accessory }.
export const reportChange = (change) => {
  lastChange = change;
};

export const forgetList = () => {
  savedList = null;
  lastChange = null;
};

// The saved list for this history entry with the change applied, or null. A
// deleted accessory sets reload: ids can move up after a delete, so the page
// is read again.
export const restoreList = (key, device) => {
  const saved = savedList;
  if (!key || !saved || saved.key !== key || String(saved.applianceId) !== String(device?.applianceId)) return null;
  const change =
    lastChange && String(lastChange.applianceId) === String(saved.applianceId) && lastChange.kind === saved.kind
      ? lastChange
      : null;
  if (change?.action === "deleted") return { ...saved, reload: true };
  if (change?.action === "saved") {
    const id = String(idOf(change.accessory, saved.kind));
    return {
      ...saved,
      items: (saved.items ?? []).map((item) => (String(idOf(item, saved.kind)) === id ? change.accessory : item)),
    };
  }
  return saved;
};
